import { useMemo } from 'react';
import { Platform } from 'react-native';

import { useResponsive } from '@sg/garnish';

import { useFeatureFlag } from '@order/LaunchDarkly';

import { DefaultHeaderLeft } from '../../../../components';
import {
  DEFAULT_SCREEN_OPTIONS,
  SCREEN_HEADER_OPTIONS,
  type ScreenOptionsMap,
} from '../../../../constants';
import { useRouteTitles } from '../../../messages';

// ─────────────────────────────────────────────────────────────────────────────

export const useAccountTabStackOptions = () => {
  const { currentBreakpoint } = useResponsive();
  const titles = useRouteTitles();

  const isStripeSdkEnabled = useFeatureFlag('CELS-4677-account-stripe-sdk-enabled'); // prettier-ignore

  // ─────────────────────────────────────────────────────────────────────

  const stackOptions = useMemo(
    () => ({
      ...DEFAULT_SCREEN_OPTIONS,
      ...SCREEN_HEADER_OPTIONS,
      headerShown: currentBreakpoint.isXS,
      headerTitle: undefined,
      headerLeft: () => <DefaultHeaderLeft fallbackHref="/account/menu" />,
    }),
    [currentBreakpoint.isXS],
  );

  // ─────────────────────────────────────────────────────────────────────

  return useMemo(() => {
    return {
      //
      // ─── Main Stack Options (applied to all screens) ─────────────────────

      Stack: stackOptions,

      // ─── Individual Tabs ─────────────────────────────────────────────────

      AccountMenu: {
        customDocumentTitle: titles.accountMenuCustomDocumentTitle,
      },
      Profile: {
        title: titles.profileTitle,
        customDocumentTitle: titles.profileCustomDocumentTitle,
      },
      PaymentAndGiftCards: isStripeSdkEnabled
        ? {
            title: titles.paymentsTitle,
            customDocumentTitle: titles.paymentsCustomDocumentTitle,
            headerShown: Platform.OS === 'web' && currentBreakpoint.isXS,
            presentation: Platform.select({
              ios: 'modal',
              default: 'transparentModal',
            }),
            contentStyle: { backgroundColor: 'transparent' },
          }
        : {
            title: titles.paymentsTitle,
            customDocumentTitle: titles.paymentsCustomDocumentTitle,
            headerShown: currentBreakpoint.isXS,
          },
      CreditAndPromoCodes: {
        title: titles.creditAndPromoCodesTitle,
        customDocumentTitle: titles.creditAndPromoCodesCustomDocumentTitle,
      },
      GiftCards: {
        title: titles.giftCardsTitle,
        customDocumentTitle: titles.accountGiftCardsCustomDocumentTitle,
      },
      Addresses: {
        title: titles.addressesTitle,
        customDocumentTitle: titles.addressesCustomDocumentTitle,
      },
      Orders: { customDocumentTitle: titles.ordersCustomDocumentTitle },
      Favorites: {
        title: titles.favoritesTitle,
        customDocumentTitle: titles.favoritesCustomDocumentTitle,
      },
      DietaryRestrictions: {
        title: titles.dietaryRestrictionsTitle,
        customDocumentTitle: titles.dietaryRestrictionsCustomDocumentTitle,
      },
      Notifications: {
        title: titles.notificationsTitle,
        customDocumentTitle: titles.notificationPreferencesCustomDocumentTitle,
      },
    } satisfies ScreenOptionsMap;
  }, [
    stackOptions,
    titles.accountMenuCustomDocumentTitle,
    titles.profileTitle,
    titles.profileCustomDocumentTitle,
    titles.paymentsTitle,
    titles.paymentsCustomDocumentTitle,
    titles.creditAndPromoCodesTitle,
    titles.creditAndPromoCodesCustomDocumentTitle,
    titles.giftCardsTitle,
    titles.accountGiftCardsCustomDocumentTitle,
    titles.addressesTitle,
    titles.addressesCustomDocumentTitle,
    titles.ordersCustomDocumentTitle,
    titles.favoritesTitle,
    titles.favoritesCustomDocumentTitle,
    titles.dietaryRestrictionsTitle,
    titles.dietaryRestrictionsCustomDocumentTitle,
    titles.notificationsTitle,
    titles.notificationPreferencesCustomDocumentTitle,
    isStripeSdkEnabled,
    currentBreakpoint.isXS,
  ]);
};
